import { commandItemIconClassCentered } from "@/components/command-palette/constants";
import { IconWorkflows } from "@/assets/icons/central/IconAutomations";
import { CommandEmpty, CommandGroup, CommandItem, CommandLoading } from "@/components/flexkit/Command";
import { useGetWorkflow, usePaginatedWorkflows } from "@/queries/automation-queries";
import { workflowDetailsUrl } from "@/utils/automations";
import type { NavigateFunction } from "react-router-dom";
import { useCallback, type FC } from "react";

type AutomationsPageProps = {
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
};

export const AutomationsPage: FC<AutomationsPageProps> = ({ runCommand, navigate }) => {
    const { workflowIds, isPending } = usePaginatedWorkflows({});

    if (isPending) {
        return <CommandLoading>Loading automations...</CommandLoading>;
    }

    if (workflowIds.length === 0) {
        return <CommandEmpty>No automations found.</CommandEmpty>;
    }

    return (
        <CommandGroup heading="Automations">
            {workflowIds.map((id) => (
                <AutomationItem key={id} workflowId={id} runCommand={runCommand} navigate={navigate} />
            ))}
        </CommandGroup>
    );
};

const AutomationItem: FC<{
    workflowId: string;
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
}> = ({ workflowId, runCommand, navigate }) => {
    const { data: workflow } = useGetWorkflow(workflowId);

    const handleSelect = useCallback(
        () => workflow && runCommand(() => navigate(workflowDetailsUrl({ id: workflow.id }))),
        [runCommand, navigate, workflow],
    );

    if (!workflow) {
        return null;
    }

    const name = workflow.metadata?.name || "Untitled automation";

    return (
        <CommandItem value={name} onSelect={handleSelect}>
            <span className={commandItemIconClassCentered}>
                <IconWorkflows />
            </span>
            <span className="truncate">{name}</span>
        </CommandItem>
    );
};
